import React, { useState, useRef, useEffect } from "react";
import logoBlack from "../raglogoblack.svg";
import logoWhite from "../raglogowhite.svg";
import { FaBars, FaBrain } from "react-icons/fa"; // for sidebar toggle
import { FiUser, FiLogOut, FiFileText, FiSettings, FiMessageCircle, FiSun, FiMoon } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/TranslationContext";
import { aiModelAPI } from '../utils/api';
import { Listbox } from '@headlessui/react';

export default function TopBar({ onLeftToggle, onOpenSettings, onOpenFiles, onOpenLogin, onOpenRegister, onOpenPayment, onShowLogoutConfirm, pdfProgress }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : true;
  });
  const [selectedModel, setSelectedModel] = useState("gpt-3.5");
  const [aiModels, setAiModels] = useState([]);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  
  const menuRef = useRef(null);
  const languageRef = useRef(null);
  const modelRef = useRef(null);
  const { user, isAuthenticated } = useAuth();
  const { t, currentLanguage, changeLanguage, availableLanguages } = useTranslation();

  // Load AI models
  useEffect(() => {
    aiModelAPI.getModels()
      .then((models) => {
        // Filter to only show active models (handle different backend representations)
        const isActiveModel = (m) => {
          const flag = m?.isActive ?? m?.active ?? m?.enabled ?? m?.is_enabled ?? m?.status;
          if (typeof flag === 'boolean') return flag;
          if (typeof flag === 'number') return flag === 1;
          if (typeof flag === 'string') {
            const v = flag.trim().toLowerCase();
            return v === 'true' || v === '1' || v === 'active' || v === 'enabled' || v === 'on' || v === 'yes';
          }
          return false;
        };
        const activeModels = (models || []).filter(isActiveModel);
        setAiModels(activeModels);
        if (
          activeModels.length > 0 &&
          (!selectedModel || selectedModel === 'gpt-3.5' || !activeModels.some((m) => m.id === selectedModel))
        ) {
          setSelectedModel(activeModels[0].id);
        }
      })
      .catch(() => setAiModels([]));
  }, []);

  // Handle dark mode toggle
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Save AI model preference
  useEffect(() => {
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    preferences.selectedModel = selectedModel;
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [selectedModel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (showLanguageDropdown && languageRef.current && !languageRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      if (showModelDropdown && modelRef.current && !modelRef.current.contains(event.target)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLanguageDropdown, showModelDropdown]);

  // Close language/model dropdowns on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setShowLanguageDropdown(false);
        setShowModelDropdown(false);
      }
    };
    if (showLanguageDropdown || showModelDropdown) {
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }
  }, [showLanguageDropdown, showModelDropdown]);

  // Capsule bar for trial period
  const safeT = typeof t === 'function' ? t : (x) => x;
  const showTrialCapsule = user && user.subscriptionStatus === 'trial';
  let subscriptionPlanName = '';
  if (user) {
    subscriptionPlanName = user.subscriptionPlan || user.activeSubscription?.name || (user.subscriptionStatus === 'trial' ? (safeT('options.trial') || 'Trial') : (safeT('options.proUser') || 'Pro User'));
  } else {
    subscriptionPlanName = safeT('options.proUser') || 'Pro User';
  }
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');

  return (
    <>
      {/* Trial Period Capsule Bar */}
      {showTrialCapsule && (
        <div className="fixed top-0 left-0 w-full flex justify-center z-[9999] select-none animate-fade-in">
          <div className="relative mt-1">
            <div className="absolute inset-0 rounded-full bg-yellow-100 blur-[8px] opacity-35 animate-capsule-glow pointer-events-none"></div>
            <div className="relative px-4 py-1.5 rounded-full bg-gradient-to-r from-yellow-200/40 via-yellow-100/30 to-yellow-200/40 shadow border border-yellow-100/60 text-yellow-800 font-medium text-sm tracking-wide flex items-center gap-2 animate-capsule-pop backdrop-blur-md pointer-events-auto" style={{boxShadow:'0 2px 12px 0 rgba(202,180,80,0.13)', backgroundColor:'rgba(255,255,224,0.32)'}}>
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4 text-yellow-400 animate-capsule-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.75a.75.75 0 10-1.5 0v4.5a.75.75 0 001.5 0v-4.5zm0 7a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" clipRule="evenodd" />
                </svg>
                <span className="text-yellow-900 font-medium text-sm" style={{opacity: 1}}>
                  {isAdmin ? (
                    <>
                      You are using trial period. Please{' '}
                      <span
                        className="font-semibold underline underline-offset-2 hover:underline cursor-pointer transition text-yellow-900 hover:opacity-80"
                        style={{textDecorationThickness:'2px'}}
                        onClick={e => { e.stopPropagation(); if (typeof onOpenPayment === 'function') onOpenPayment(); }}
                      >
                        upgrade to pro
                      </span>
                      .
                    </>
                  ) : (
                    'You are using trial period. Please contact your administrator.'
                  )}
                </span>
              </span>
            </div>
          </div>
          <style>{`
            @keyframes capsule-glow { 0%,100%{opacity:0.35;filter:blur(8px);} 50%{opacity:0.5;filter:blur(14px);} }
            .animate-capsule-glow { animation: capsule-glow 2.5s infinite alternate; }
            @keyframes capsule-pop { 0%{transform:scale(0.95);opacity:0;} 60%{transform:scale(1.03);opacity:1;} 100%{transform:scale(1);} }
            .animate-capsule-pop { animation: capsule-pop 0.7s cubic-bezier(0.68,-0.55,0.27,1.55); }
            @keyframes capsule-pulse { 0%,100%{opacity:0.7;} 50%{opacity:1;} }
            .animate-capsule-pulse { animation: capsule-pulse 1.5s infinite; }
            @keyframes fade-in { 0%{opacity:0;} 100%{opacity:1;} }
            .animate-fade-in { animation: fade-in 0.5s; }
          `}</style>
        </div>
      )}
      <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-b border-gray-200/20 dark:border-gray-700/20 shadow-lg px-4 sm:px-6 py-4 flex justify-between items-center relative z-50 group">
        {/* Ultra‑subtle purple separation hue */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-500/3 via-transparent to-transparent dark:from-purple-400/5 dark:via-transparent dark:to-transparent transition-colors duration-300 group-hover:from-purple-500/6"></div>
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-400/30 to-transparent dark:via-purple-300/20"></div>
        </div>
        
        {/* Left: Sidebar toggle + Logo */}
        <div className="flex items-center gap-4 relative z-10">
          {/* ☰ Sidebar Toggle Button */}
          <button
            onClick={onLeftToggle}
            className="p-2.5 rounded-xl text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-gray-800 transition-all duration-300 simple-hover-lift"
          >
            <FaBars size={18} />
          </button>

          {/* Enhanced Prominent Logo */}
          <div className="flex items-center gap-3">
            <div className="relative header-logo-container logo-container-hover">
              {/* Glowing background effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-lg blur-sm group-hover:blur-md transition-all duration-300 header-logo-glow"></div>
              
              {/* Logo container with premium styling */}
              <div className="relative premium-logo-frame group transition-all duration-300 hover:scale-105">
                <img 
                  src={logoBlack} 
                  alt="RAG AI Logo Light" 
                  className="h-10 w-auto block dark:hidden filter drop-shadow-sm header-logo-pulse" 
                />
                <img 
                  src={logoWhite} 
                  alt="RAG AI Logo Dark" 
                  className="h-10 w-auto hidden dark:block filter drop-shadow-sm header-logo-pulse" 
                />
              </div>
              
              {/* Animated accent dot */}
              <div className="logo-accent-dot"></div>
            </div>
            
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent tracking-tight">
                RAG AI
              </h1>
              <p className="text-xs bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent font-medium tracking-wide uppercase">
                Engineering Platform
              </p>
            </div>
          </div>
        </div>

        {/* Right: Controls & Profile */}
        <div className="flex items-center gap-3 relative z-10">
          {/* Global PDF Processing Capsule */}
          {pdfProgress && (
            <div className="hidden sm:flex items-center">
              <div
                className="px-3 py-1 rounded-full bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-200/60 dark:border-indigo-800/40 text-indigo-700 dark:text-indigo-300 text-xs font-semibold flex items-center gap-2 shadow-sm"
                title={`${pdfProgress.step || 'Processing'}${typeof pdfProgress.percent === 'number' ? ` • ${pdfProgress.percent}%` : ''}`}
              >
                <div className="relative w-4 h-4">
                  <div className="absolute inset-0 rounded-full border-2 border-indigo-400/40"></div>
                  <div className="absolute inset-0 rounded-full border-2 border-t-indigo-500 animate-spin"></div>
                </div>
                <span className="truncate max-w-[220px]">
                  {pdfProgress.documentName || 'Document'} • {pdfProgress.step || 'Processing'}{typeof pdfProgress.percent === 'number' ? ` (${Math.round(pdfProgress.percent)}%)` : ''}
                </span>
              </div>
            </div>
          )}
          {/* Subscription pill - first item on the right */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center">
              <span
                className="px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs font-semibold border border-blue-200/60 dark:border-blue-800/40"
                title="Current subscription"
              >
                {subscriptionPlanName}
              </span>
            </div>
          )}
          {/* 🌙 Dark/Light Mode Toggle */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-white/80 to-gray-50/80 dark:from-gray-800/80 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all duration-300 simple-hover-lift hover:shadow-lg hover:shadow-indigo-500/20 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/10 group-hover:to-purple-500/5 transition-all duration-300"></div>
            {isDarkMode ? (
              <FiSun size={18} className="relative z-10 transform group-hover:rotate-12 transition-transform duration-300" />
            ) : (
              <FiMoon size={18} className="relative z-10 transform group-hover:rotate-12 transition-transform duration-300" />
            )}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-400/0 to-purple-400/0 group-hover:from-indigo-400/20 group-hover:to-purple-400/10 blur-sm transition-all duration-300"></div>
          </button>

          {/* 🌐 Language Selector */}
          <div className="relative" ref={languageRef}>
            <button
              onClick={() => {
                setShowLanguageDropdown(!showLanguageDropdown);
                if (!showLanguageDropdown) setShowModelDropdown(false);
              }}
              className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-white/80 to-gray-50/80 dark:from-gray-800/80 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-all duration-300 simple-hover-lift hover:shadow-lg hover:shadow-green-500/20 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500/30"
              title="Language"
            >
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-green-500/0 to-emerald-500/0 group-hover:from-green-500/10 group-hover:to-emerald-500/5 transition-all duration-300"></div>
              <svg className="w-4 h-4 relative z-10" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7 2a1 1 0 011 1v1h3a1 1 0 110 2H9.578a18.87 18.87 0 01-1.724 4.78c.29.354.596.696.914 1.026a1 1 0 11-1.44 1.389c-.188-.196-.373-.396-.554-.6a19.098 19.098 0 01-3.107 3.567 1 1 0 01-1.334-1.49 17.087 17.098 0 003.13-3.733 18.992 18.992 0 01-1.487-2.494 1 1 0 111.79-.89c.234.47.489.928.764 1.372.417-.934.752-1.913.997-2.927H3a1 1 0 110-2h3V3a1 1 0 011-1zm6 6a1 1 0 01.894.553l2.991 5.982a.869.869 0 01.02.037l.99 1.98a1 1 0 11-1.79.895L15.383 16h-4.764l-.724 1.447a1 1 0 11-1.788-.894l.99-1.98.019-.038 2.99-5.982A1 1 0 0113 8zm-1.382 6h2.764L13 11.236 11.618 14z" clipRule="evenodd" />
              </svg>
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400/0 to-emerald-400/0 group-hover:from-green-400/20 group-hover:to-emerald-400/10 blur-sm transition-all duration-300"></div>
            </button>

            {showLanguageDropdown && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-[99999] border border-gray-200/50 dark:border-gray-700/50 overflow-hidden backdrop-blur-xl dropdown-slide-in">
                <div className="py-2">
                  {availableLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setShowLanguageDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200 ${
                        currentLanguage === lang.code ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-semibold' : ''
                      }`}
                    >
                      <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{lang.code.toUpperCase()}</span>
                      </div>
                      <span className="text-sm">{lang.nativeName}</span>
                      {currentLanguage === lang.code && (
                        <svg className="w-4 h-4 text-green-500 ml-auto" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 🤖 AI Model Selector */}
          <div className="relative" ref={modelRef}>
            <button
              onClick={() => {
                setShowModelDropdown(!showModelDropdown);
                if (!showModelDropdown) setShowLanguageDropdown(false);
              }}
              className="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-white/80 to-gray-50/80 dark:from-gray-800/80 dark:to-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-orange-600 dark:hover:text-orange-400 transition-all duration-300 simple-hover-lift hover:shadow-lg hover:shadow-orange-500/20 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
              title="AI Model"
            >
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-orange-500/0 to-red-500/0 group-hover:from-orange-500/10 group-hover:to-red-500/5 transition-all duration-300"></div>
              <FaBrain size={16} className="relative z-10" />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-400/0 to-red-400/0 group-hover:from-orange-400/20 group-hover:to-red-400/10 blur-sm transition-all duration-300"></div>
            </button>

            {showModelDropdown && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-[99999] border border-gray-200/50 dark:border-gray-700/50 overflow-hidden backdrop-blur-xl dropdown-slide-in">
                <div className="py-2">
                  {aiModels.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => {
                        setSelectedModel(model.id);
                        setShowModelDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200 ${
                        selectedModel === model.id ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 font-semibold' : ''
                      }`}
                    >
                      <div className="w-5 h-5 bg-orange-500 rounded-lg flex items-center justify-center">
                        <FaBrain className="w-3 h-3 text-white" />
                      </div>
                      <div className="flex-1 text-left">
                        <div className="text-sm font-medium">{model.displayName}</div>
                        {model.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{model.description}</div>
                        )}
                      </div>
                      {selectedModel === model.id && (
                        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Profile button with dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-200 font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500/50 shadow-md hover:shadow-lg transition-all duration-300 simple-hover-lift border border-gray-200/50 dark:border-gray-600/50"
            >
              <FiUser size={16} />
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-[99999] border border-gray-200/50 dark:border-gray-700/50 overflow-hidden backdrop-blur-xl dropdown-slide-in">
                {/* User info section - only show if authenticated */}
                {isAuthenticated && user && (
                  <div className="px-5 py-4 border-b border-gray-200/30 dark:border-gray-700/30 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-gray-700/30 dark:to-gray-600/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-sm font-bold">{user.firstName?.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-gray-800 dark:text-gray-100">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                        <div className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 font-medium">
                          {user && user.subscriptionStatus === 'trial'
                            ? `🎯 ${(safeT('options.trial') || 'Trial')}`
                            : `⭐ ${subscriptionPlanName || (safeT('options.proUser') || 'Pro User')}`}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="py-2">
                  {/* Authentication options - only show if not authenticated */}
                  {!isAuthenticated && (
                    <>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          onOpenLogin();
                        }}
                        className="menu-item w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-gray-700/50 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200"
                      >
                        <div className="w-5 h-5 bg-green-500 rounded-lg flex items-center justify-center">
                          <FiUser className="w-3 h-3 text-white" />
                        </div>
                        {t('nav.login')}
                      </button>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          onOpenRegister();
                        }}
                        className="menu-item w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-gray-700/50 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200"
                      >
                        <div className="w-5 h-5 bg-blue-500 rounded-lg flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </div>
                        {t('nav.register')}
                      </button>
                      <div className="h-px bg-gray-200 dark:bg-gray-700 my-2 mx-3"></div>
                    </>
                  )}

                  {/* Common options for all users */}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onOpenFiles();
                    }}
                    className="menu-item w-full text-left px-5 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200"
                  >
                    <div className="w-5 h-5 bg-blue-500 rounded-lg flex items-center justify-center">
                      <FiFileText className="w-3 h-3 text-white" />
                    </div>
                    {t('nav.files')}
                  </button>
                  {/* Manage Employees - Admin Only */}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        // This will be handled by the parent component
                        if (typeof window.openEmployeesModal === 'function') {
                          window.openEmployeesModal();
                        }
                      }}
                      className="menu-item w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200"
                    >
                      <div className="w-5 h-5 bg-indigo-500 rounded-lg flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                        </svg>
                      </div>
                      Manage Employees
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onOpenSettings();
                    }}
                    className="menu-item w-full text-left px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 flex items-center gap-3 text-gray-700 dark:text-gray-200"
                  >
                    <div className="w-5 h-5 bg-gray-500 rounded-lg flex items-center justify-center">
                      <FiSettings className="w-3 h-3 text-white" />
                    </div>
                    {t('nav.settings')}
                  </button>

                  {/* Upgrade option - only show for admin/super_admin */}
                  {user && (user.role === 'admin' || user.role === 'super_admin') && (
                    <>
                      <div className="h-px bg-gray-200 dark:bg-gray-700 my-2 mx-3"></div>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          onOpenPayment();
                        }}
                        className="menu-item w-full text-left px-5 py-3 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all duration-200 flex items-center gap-3 text-amber-700 dark:text-amber-400 font-medium"
                      >
                        <div className="w-5 h-5 bg-amber-500 rounded-lg flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </div>
                        {user.subscriptionStatus === 'trial' ? 'Upgrade to Pro' : 'Upgrade your plan'}
                      </button>
                    </>
                  )}

                  {/* Logout option - only show if authenticated */}
                  {isAuthenticated && (
                    <>
                      <div className="h-px bg-gray-200 dark:bg-gray-700 my-2 mx-3"></div>
                      <button
                        onClick={() => {
                          setIsMenuOpen(false);
                          onShowLogoutConfirm();
                        }}
                        className="menu-item w-full text-left px-5 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 flex items-center gap-3 text-red-600 dark:text-red-400 font-medium"
                      >
                        <div className="w-5 h-5 bg-red-500 rounded-lg flex items-center justify-center">
                          <FiLogOut className="w-3 h-3 text-white" />
                        </div>
                        {t('nav.logout')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* (subscription pill placed at start) */}
        </div>
      </div>

      {/* Outside click handled via document listener to avoid overlay click conflicts */}
    </>
  );
}
